const STRFTIME: Record<string, string> = {
    a: "ccc",
    A: "cccc",
    b: "MMM",
    B: "MMMM",
    d: "dd",
    H: "HH",
    I: "hh",
    j: "o",
    m: "MM",
    M: "mm",
    p: "a",
    S: "ss",
    W: "WW",
    w: "c",
    y: "yy",
    Y: "yyyy",
    c: "ccc MMM d HH:mm:ss yyyy",
    x: "MM/dd/yy",
    X: "HH:mm:ss",
};

const NUMBERING: [RegExp, string][] = [
    [/^ar-(sa|sy|001)$/i, "arab"],
    [/^bn/i, "beng"],
    [/^bo/i, "tibt"],
    [/^pa-in/i, "guru"],
    [/^ta/i, "tamldec"],
    [/.*/i, "latn"],
];

const TOKENS = ["cccc", "ccc", "MMMM", "MMM", "yyyy", "yy", "dd", "MM", "HH", "hh", "mm", "ss", "WW", "a", "o", "c", "d"];

type Piece = { literal: string } | { token: string };

interface Settings {
    locale: string;
    numbering: string;
    pieces: Piece[];
}

let settings: Settings = build("en-US", "%m/%d/%Y", "%H:%M:%S");
const cache = new Map<string, Intl.NumberFormat>();

function toLuxon(format: string): string {
    const output: string[] = [];
    let inToken = false;
    for (const raw of format) {
        let character = raw;
        if (character === "%" && !inToken) {
            inToken = true;
            continue;
        }
        if (/[a-z]/i.test(character)) {
            character = inToken && STRFTIME[character] !== undefined ? STRFTIME[character] as string : `'${character}'`;
        }
        output.push(character);
        inToken = false;
    }
    return output.join("");
}

function parse(format: string): Piece[] {
    const pieces: Piece[] = [];
    let index = 0;
    while (index < format.length) {
        const character = format[index] as string;
        if (character === "'") {
            const end = format.indexOf("'", index + 1);
            const stop = end === -1 ? format.length : end;
            pieces.push({ literal: format.slice(index + 1, stop) });
            index = stop + 1;
            continue;
        }
        const token = TOKENS.find((candidate) => format.startsWith(candidate, index));
        if (token) {
            pieces.push({ token });
            index += token.length;
            continue;
        }
        pieces.push({ literal: character });
        index += 1;
    }
    return pieces;
}

function build(locale: string, dateFormat: string, timeFormat: string): Settings {
    const numbering = (NUMBERING.find(([pattern]) => pattern.test(locale)) ?? [/.*/, "latn"])[1];
    return { locale, numbering, pieces: parse(`${toLuxon(dateFormat)} ${toLuxon(timeFormat)}`) };
}

export function setDateTimeLocale(lang: string, dateFormat?: string | null, timeFormat?: string | null) {
    settings = build(lang.replace(/_/g, "-"), dateFormat || "%m/%d/%Y", timeFormat || "%H:%M:%S");
}

function digits(value: number, width: number): string {
    const key = `${settings.locale}|${settings.numbering}|${width}`;
    let format = cache.get(key);
    if (!format) {
        try {
            format = new Intl.NumberFormat(settings.locale, { numberingSystem: settings.numbering,
                                                              minimumIntegerDigits: width, useGrouping: false });
        } catch {
            format = new Intl.NumberFormat("en-US", { minimumIntegerDigits: width, useGrouping: false });
        }
        cache.set(key, format);
    }
    return format.format(value);
}

function named(date: Date, options: Intl.DateTimeFormatOptions): string {
    try {
        return new Intl.DateTimeFormat(settings.locale, options).format(date);
    } catch {
        return new Intl.DateTimeFormat("en-US", options).format(date);
    }
}

function meridiem(date: Date): string {
    const probe = new Date(Date.UTC(2016, 10, 13, date.getHours() < 12 ? 9 : 19));
    try {
        const parts = new Intl.DateTimeFormat(settings.locale, { hour: "numeric", hourCycle: "h12", timeZone: "UTC" })
            .formatToParts(probe);
        const period = parts.find((part) => part.type === "dayPeriod");
        if (period) {
            return period.value;
        }
    } catch {
        return date.getHours() < 12 ? "AM" : "PM";
    }
    return date.getHours() < 12 ? "AM" : "PM";
}

function isoWeek(date: Date): number {
    const target = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const day = target.getUTCDay() || 7;
    target.setUTCDate(target.getUTCDate() + 4 - day);
    const start = new Date(Date.UTC(target.getUTCFullYear(), 0, 1));
    return Math.ceil(((target.getTime() - start.getTime()) / 86400000 + 1) / 7);
}

function render(date: Date, token: string): string {
    switch (token) {
    case "dd":
        return digits(date.getDate(), 2);
    case "d":
        return digits(date.getDate(), 1);
    case "MM":
        return digits(date.getMonth() + 1, 2);
    case "MMM":
        return named(date, { month: "short" });
    case "MMMM":
        return named(date, { month: "long" });
    case "yyyy":
        return digits(date.getFullYear(), 4);
    case "yy":
        return digits(date.getFullYear() % 100, 2);
    case "HH":
        return digits(date.getHours(), 2);
    case "hh":
        return digits(date.getHours() % 12 === 0 ? 12 : date.getHours() % 12, 2);
    case "mm":
        return digits(date.getMinutes(), 2);
    case "ss":
        return digits(date.getSeconds(), 2);
    case "a":
        return meridiem(date);
    case "ccc":
        return named(date, { weekday: "short" });
    case "cccc":
        return named(date, { weekday: "long" });
    case "c":
        return digits(date.getDay() || 7, 1);
    case "o": {
        const start = new Date(date.getFullYear(), 0, 1);
        return digits(Math.floor((date.getTime() - start.getTime()) / 86400000) + 1, 3);
    }
    case "WW":
        return digits(isoWeek(date), 2);
    default:
        return token;
    }
}

export function formatDateTime(date: Date): string {
    return settings.pieces.map((piece) => ("token" in piece ? render(date, piece.token) : piece.literal)).join("");
}

export function formatServerDateTime(value: string): string {
    if (!value) {
        return "";
    }
    const parsed = new Date(`${value.replace(" ", "T")}${/[zZ]|[+-]\d\d:?\d\d$/.test(value) ? "" : "Z"}`);
    return Number.isNaN(parsed.getTime()) ? value : formatDateTime(parsed);
}
